/**
 * Deterministic node and style ids.
 *
 * Real Webflow ids look like `a7cb7db0-e6ac-857c-f1fb-eed5c01819f6` — hex in UUID grouping,
 * but not RFC-4122 (note the third group starts with `8`, not the `4` a v4 UUID requires).
 * So we match the shape without pretending to be a v4.
 *
 * We derive them from a stable structural path rather than a random source. Random ids would
 * make every golden snapshot churn completely on each run, which would turn the regression
 * suite into noise — and the whole point of that suite is to spot a Webflow format change as
 * a small, readable diff.
 *
 * ⚠️ Unverified: we believe Webflow only requires ids to be unique *within* a payload, not
 * globally unique or genuinely random. Spike S1 confirms this against a real Designer before
 * anything ships.
 */

const FNV_PRIME = 0x01000193;
const FNV_OFFSET = 0x811c9dc5;

function fnv1a(input: string, salt: number): number {
  let hash = (FNV_OFFSET ^ salt) >>> 0;
  for (let i = 0; i < input.length; i++) {
    hash ^= input.charCodeAt(i);
    hash = Math.imul(hash, FNV_PRIME) >>> 0;
  }
  // Final avalanche, so single-character differences spread across the whole word.
  hash ^= hash >>> 16;
  hash = Math.imul(hash, 0x7feb352d) >>> 0;
  hash ^= hash >>> 15;
  hash = Math.imul(hash, 0x846ca68b) >>> 0;
  hash ^= hash >>> 16;
  return hash >>> 0;
}

const hex8 = (n: number): string => n.toString(16).padStart(8, '0');

/** 32 hex characters from four independently salted rounds. */
function hash128(seed: string): string {
  return hex8(fnv1a(seed, 0)) + hex8(fnv1a(seed, 1)) + hex8(fnv1a(seed, 2)) + hex8(fnv1a(seed, 3));
}

/**
 * Build an id from a structural seed.
 *
 * Seeds read like `HeroSplit#0/copy/h1` or `style:lp-h2`, so a diff in a golden file points
 * straight at the node that changed.
 */
export function makeId(seed: string): string {
  const h = hash128(seed);
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20, 32)}`;
}

export const styleSeed = (className: string): string => `style:${className}`;
